import * as yup from 'yup';

import { AccountPriority } from '../../../../common/models/enums/accountPriority';
import { isValidStringUrl } from '../../../../common/utils/url.helper';
import { useTranslations } from '../../../../contexts/LocalizationContext';
import {
  MAX_COMMON_LENGTH,
  MAX_RESTAURANT_ADDRESS_LENGTH,
  MAX_RESTAURANT_ANDROID_LINK_LENGTH,
  MAX_RESTAURANT_DETAIL_LENGTH,
  MAX_RESTAURANT_NAME_LENGTH,
  MAX_RESTAURANT_PHONE_LENGTH,
  MAX_RESTAURANT_WHOLESALE_CUSTOMER_ID_LENGTH,
} from '../../restaurant.consts';
import { RestaurantStatus, StarterSetSize } from '../../RestaurantForm/RestaurantForm.form';

export const useRestaurantsUpdateFormValidationSchema = (): yup.AnyObjectSchema => {
  const translations = useTranslations();

  const selectOptionSchema = {
    id: yup.string(),
    translations: yup.object().shape({
      en: yup.string(),
      de: yup.string(),
      fr: yup.string().nullable(),
    }),
  };

  const validateUrlProperty = (maxLength?: number) => {
    return yup
      .string()
      .nullable()
      .max(maxLength ?? MAX_COMMON_LENGTH, translations('validation_too_long'))
      .test('test url', translations('validation_incorrect_url_format'), (text) => {
        if (text?.trim().length === 0 || !text) {
          return true;
        }
        return text.trim().length > 0 && isValidStringUrl(text.trim());
      });
  };

  return yup.object().shape({
    restaurantName: yup.string().nullable().max(MAX_RESTAURANT_NAME_LENGTH, translations('validation_too_long')),
    restaurantDetail: yup.string().nullable().max(MAX_RESTAURANT_DETAIL_LENGTH, translations('validation_too_long')),
    address: yup
      .object()
      .nullable()
      .shape({
        street: yup.string().nullable().max(MAX_COMMON_LENGTH, translations('validation_too_long')),
        streetNumber: yup.string().nullable().max(MAX_COMMON_LENGTH, translations('validation_too_long')),
        postalCode: yup.string().nullable().max(MAX_COMMON_LENGTH, translations('validation_too_long')),
        city: yup.object().nullable().shape(selectOptionSchema),
        formattedAddress: yup
          .string()
          .nullable()
          .max(MAX_RESTAURANT_ADDRESS_LENGTH, translations('validation_too_long')),
      }),
    addressLinkAndroid: validateUrlProperty(MAX_RESTAURANT_ANDROID_LINK_LENGTH),
    phoneNumber: yup.string().nullable().max(MAX_RESTAURANT_PHONE_LENGTH, translations('validation_too_long')),
    websiteLink: validateUrlProperty(),
    status: yup.mixed().nullable().oneOf(Object.values(RestaurantStatus), translations('validation_incorrect_value')),
    starterSetStartDate: yup.date().nullable(),
    starterSetSize: yup
      .mixed()
      .nullable()
      .oneOf(Object.values(StarterSetSize), translations('validation_incorrect_value')),
    wholesaleCustomerId: yup
      .string()
      .nullable()
      .max(MAX_RESTAURANT_WHOLESALE_CUSTOMER_ID_LENGTH, translations('validation_too_long')),
    deliverers: yup.object().shape({
      uberEats: yup.object().shape({
        url: validateUrlProperty(),
      }),
      lieferando: yup.object().shape({
        url: validateUrlProperty(),
      }),
      wolt: yup.object().shape({
        url: validateUrlProperty(),
      }),
      customDelivery: yup.object().shape({
        url: validateUrlProperty(),
      }),
    }),
    accountPriority: yup
      .mixed()
      .nullable()
      .oneOf(Object.values(AccountPriority), translations('validation_incorrect_value')),
    keyAccountId: yup.string().nullable(),
    closingSourceId: yup.string().nullable(),
    latitude: yup.string().nullable(),
    longitude: yup.string().nullable(),
  });
};
